import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { CategoryUsage, deleteCategory, fetchCategoryUsage } from '../../services/knowledgeApi';

interface Props {
  category: { id: string; name: string };
  onClose: () => void;
  onDeleted: (message: string) => void;
}

/** Confirm deleting a knowledge category, showing what it affects first. */
export const DeleteCategoryDialog: React.FC<Props> = ({ category, onClose, onDeleted }) => {
  const { getIdToken } = useAuth();
  const [usage, setUsage] = useState<CategoryUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'keep' | 'delete'>('keep');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCategoryUsage(getIdToken, category.id).then(setUsage).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.id]);

  const confirm = async () => {
    setDeleting(true);
    setError(null);
    try {
      const res = await deleteCategory(getIdToken, category.id, mode === 'delete');
      const parts = [`Category “${category.name}” deleted`];
      if (res.materials_deleted) parts.push(`${res.materials_deleted} file(s) deleted`);
      if (res.materials_uncategorized) parts.push(`${res.materials_uncategorized} file(s) kept without a category`);
      onDeleted(parts.join('. ') + '.');
    } catch (e: any) {
      setError(e.message);
      setDeleting(false);
    }
  };

  const n = usage?.materials ?? 0;

  return (
    <Dialog open onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete category “{category.name}”?</DialogTitle>
      <DialogContent dividers>
        {!usage && !error && <CircularProgress size={24} />}
        {usage && (
          <Stack spacing={2}>
            {usage.managed ? (
              <Alert severity="error">
                This category holds self-improvement knowledge, so it can't be deleted here. Turn off learning for its target on the
                Learning page instead.
              </Alert>
            ) : (
              <>
                {n === 0 ? (
                  <Typography variant="body2">This category is empty.</Typography>
                ) : (
                  <>
                    <Typography variant="body2">
                      It has {n} file{n === 1 ? '' : 's'}. What should happen to {n === 1 ? 'it' : 'them'}?
                    </Typography>
                    <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'keep' | 'delete')}>
                      <FormControlLabel
                        value="keep"
                        control={<Radio />}
                        label={`Keep ${n === 1 ? 'the file' : 'the files'} without a category (not searchable until you give ${n === 1 ? 'it' : 'them'} a new one)`}
                      />
                      <FormControlLabel
                        value="delete"
                        control={<Radio color="error" />}
                        label={<Typography color="error.main">Delete {n === 1 ? 'the file' : `all ${n} files`} permanently</Typography>}
                      />
                    </RadioGroup>
                  </>
                )}
                {usage.chains.length > 0 && (
                  <Alert severity="warning">
                    {usage.chains.length === 1 ? 'This chain searches' : 'These chains search'} this category and will stop getting
                    knowledge from it: {usage.chains.map((c) => c.name).join(', ')}.
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                  This can't be undone.
                </Typography>
              </>
            )}
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={!usage || usage.managed || deleting}>
          {deleting ? 'Deleting…' : mode === 'delete' && n > 0 ? `Delete category and ${n} file${n === 1 ? '' : 's'}` : 'Delete category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
